import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { useFormContext } from "react-hook-form";
import {
  DatePickerElement,
  SelectElement,
  SwitchElement,
  TextFieldElement,
} from "react-hook-form-mui";
import ImageInput from "../../../components/ImageInput";
import { Switch } from "../../../components/Switch";
import { useTranslate } from "../../../hooks/useTranslate";
import {
  getAllBatchs,
  getAllFlocks,
  loadBreed,
  loadIntership,
  loadLactation,
  loadReproductiveStatus,
} from "../../../services/animals";
import { Aptitude, Gender } from "../enum";
import { IOptionTypeAptitudes, IOptionTypeGenderAnimal } from "../interface";
import { capitalize, get } from "lodash";
import { isValid } from "date-fns";

export interface Race {
  id: string;
  language: {
    "pt-br": string;
    "en-us": string;
    "es-es": string;
  };
}
export interface Intership {
  id: string;
  gender: "male" | "female";
  type: "milk" | "cut";
  language: {
    "pt-br": string;
    "en-us": string;
    "es-es": string;
  };
}
export interface Lactation {
  id: string;
  language: {
    "pt-br": string;
    "en-us": string;
    "es-es": string;
  };
}
export interface Situation {
  id: string;
  language: {
    "pt-br": string;
    "en-us": string;
    "es-es": string;
  };
}
export interface OptionSelect {
  label: string;
  id: string;
}

export interface IAnimalForm {
  property: string;
  isNew: boolean;
}

const AnimalForm: React.FC<IAnimalForm> = ({ property, isNew }) => {
  const { translate, currentLanguage } = useTranslate();
  const form = useFormContext<any>();

  const values = form.watch();

  const [situations, setSituations] = useState<Situation[]>([]);
  const [interships, setInterships] = useState<Intership[]>([]);
  const [lactations, setLactations] = useState<Lactation[]>([]);
  const [races, setRaces] = useState<Race[]>([]);
  const [flocks, setFlocks] = useState<OptionSelect[]>([]);
  const [batchs, setBatchs] = useState<OptionSelect[]>([]);

  const animalGenders: IOptionTypeGenderAnimal[] = [
    { value: Gender.MALE, label: translate("animals.male") },
    { value: Gender.FEMALE, label: translate("animals.female") },
  ];

  const animalAptitudes: IOptionTypeAptitudes[] = [
    { value: Aptitude.CUT, label: translate("animals.aptitude_cut") },
    { value: Aptitude.MILK, label: translate("animals.aptitude_milk") },
  ];

  const internshipOptions = useMemo(() => {
    return interships
      .filter((i) => {
        return i.type === values.type;
      })
      .filter((i) => i.gender === values.gender)
      .map((i) => ({
        id: i.id,
        label: get(i.language, currentLanguage.code) ?? i.language["en-us"],
      }));
  }, [values.type, values.gender, interships, currentLanguage.code]);

  const lactationOptions = useMemo(() => {
    return lactations.map((i) => ({
      id: i.id,
      label: capitalize(
        get(i.language, currentLanguage.code) ?? i.language["en-us"]
      ),
    }));
  }, [lactations, currentLanguage.code]);

  const animalSituationOptions = useMemo(() => {
    return situations.map((i) => ({
      id: i.id,
      label: get(i.language, currentLanguage.code) ?? i.language["en-us"],
    }));
  }, [currentLanguage.code, situations]);

  const racesOptions = useMemo(() => {
    return races.map((i) => ({
      id: i.id,
      label: get(i.language, currentLanguage.code) ?? i.language["en-us"],
    }));
  }, [currentLanguage.code, races]);

  const fetchBatchs = useCallback(async () => {
    const data = await getAllBatchs(property);
    setBatchs(
      data.map((d: any) => ({
        id: d.id,
        label: d.name,
      }))
    );
  }, [property]);

  const fetchInterships = useCallback(async () => {
    const interships = await loadIntership();
    setInterships(interships);
  }, []);

  const fetchLactations = useCallback(async () => {
    const lactations = await loadLactation();
    setLactations(lactations);
  }, []);

  const fetchReproductiveStatus = useCallback(async () => {
    const reproductiveStatus = await loadReproductiveStatus();
    setSituations(reproductiveStatus);
  }, []);

  const fetchRaces = useCallback(async () => {
    const breeds = await loadBreed();
    setRaces(breeds);
  }, []);

  const fetchFlocks = useCallback(async () => {
    const data = await getAllFlocks(property);
    setFlocks(
      data.map((d: any) => ({
        id: d.id,
        label: d.name,
      }))
    );
  }, [property]);

  useEffect(() => {
    fetchBatchs();
    fetchFlocks();
    fetchRaces();
    fetchInterships();
    fetchLactations();
    fetchReproductiveStatus();
  }, [
    fetchBatchs,
    fetchFlocks,
    fetchInterships,
    fetchLactations,
    fetchRaces,
    fetchReproductiveStatus,
  ]);

  useEffect(() => {
    if (values.type === undefined) {
      form.setValue("type", Aptitude.CUT);
    }
  }, [values, form]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={6}>
          <Switch
            currentActive={form.watch("gender")}
            options={animalGenders}
            onChange={(value) => {
              form.setValue("gender", value);
              // if (value === Gender.MALE) {
              // form.setValue("type", Aptitude.CUT);
              // }
            }}
          />
        </Grid>
        <Grid item md={6}>
          <Switch
            // disabled={form.watch("gender") !== Gender.FEMALE}
            currentActive={form.watch("type") ?? null}
            options={animalAptitudes}
            onChange={(value) => {
              form.setValue("type", value);
            }}
          />
        </Grid>
        <Grid item md={8}>
          <TextFieldElement
            name="name"
            label={translate("animals.placeholder_name_animal")}
            type="text"
          />
        </Grid>
        <Grid item md={4}>
          <TextFieldElement
            name="identifier"
            label={translate("animals.placeholder_number_identification")}
          />
        </Grid>
        <Grid item md={4}>
          <DatePickerElement
            inputFormat="dd/MM/yyyy"
            name="dob"
            label={translate("global.placeholder_dob")}
            parseDate={(date, keyboardInputValue) => {
              if (!date && !keyboardInputValue) return null;
              if (keyboardInputValue?.length === 10 && !isValid(date)) {
                return null;
              }
              return new Date(new Date(date).setHours(0, 0, 0, 0));
            }}
          />
        </Grid>
        <Grid item md={4}>
          <DatePickerElement
            inputFormat="dd/MM/yyyy"
            disabled={!isNew}
            name="date_weighing"
            label={translate("animals.placeholder_date_weighing")}
            parseDate={(date, keyboardInputValue) => {
              if (!date && !keyboardInputValue) return null;
              if (keyboardInputValue?.length === 10 && !isValid(date)) {
                return null;
              }
              return new Date(new Date(date).setHours(0, 0, 0, 0));
            }}
          />
        </Grid>
        <Grid item md={4}>
          <TextFieldElement
            disabled={!isNew}
            name="weighing"
            label={translate("animals.placeholder_weighing")}
            type="number"
          />
        </Grid>
        <Grid item md={6}>
          <SelectElement
            name="internship"
            options={internshipOptions}
            label={translate("animals.placeholder_internship")}
          />
        </Grid>
        <Grid item md={6}>
          <SelectElement
            name="race"
            options={racesOptions}
            label={translate("animals.placeholder_race")}
          />
        </Grid>
        {/* {form.watch("race") === "other" && (
          <Grid item md={3}>
            <TextFieldElement
              name="race_other"
              label={translate("animals.placeholder_new_race")}
            />
          </Grid>
        )} */}
      </Grid>
      <Grid container sx={{ mt: 0.5 }} spacing={2}>
        {form.watch("gender") === Gender.FEMALE &&
          form.watch("type") === Aptitude.CUT && (
            <>
              <Grid item md={3}>
                <SelectElement
                  name="situation"
                  options={animalSituationOptions}
                  label={translate("animals.placeholder_situation")}
                />
              </Grid>
              <Grid item md={3}>
                <SelectElement
                  name="lactation"
                  options={lactationOptions}
                  label={translate("animals.placeholder_lactation")}
                />
              </Grid>
            </>
          )}
        <Grid item md>
          <TextFieldElement
            name="description"
            label={translate("animals.placeholder_description")}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {batchs.length > 0 && (
          <Grid item md={6}>
            <SelectElement
              name="batch"
              options={batchs}
              label={translate("animals.placeholder_batch")}
            />
          </Grid>
        )}
        {flocks.length > 0 && (
          <Grid item md={6}>
            <SelectElement
              name="flock"
              options={flocks}
              label={translate("animals.placeholder_flock")}
            />
          </Grid>
        )}
        <Grid item md={12}>
          <ImageInput label={translate("animals.add_image")} name="photo" />
        </Grid>
      </Grid>
      {form.watch("gender") === Gender.MALE && (
        <Paper sx={{ bgcolor: orange[50], p: 2, mt: 2 }} elevation={0}>
          <Stack
            width={1}
            spacing={2}
            direction="column"
            justifyContent="space-between"
          >
            <Stack
              width={1}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontWeight: "bold", color: "warning.main" }}>
                {translate("animals.breeding")}
              </Typography>
              <Box sx={{ mr: -2 }}>
                <SwitchElement
                  disableTypography={true}
                  label=""
                  name="breeding_animal"
                />
              </Box>
            </Stack>

            <Typography sx={{ color: "warning.main" }}>
              {translate("animals.description_breeding")}
            </Typography>
          </Stack>
        </Paper>
      )}
      <Stack justifyContent="flex-end" direction="row">
        <Button
          color="secondary"
          type="submit"
          variant="contained"
          disabled={form.formState.isSubmitting}
          sx={{ mt: 3, textTransform: "capitalize" }}
        >
          {translate(isNew ? "animals.btn_create" : "animals.btn_update")}
        </Button>
      </Stack>
    </>
  );
};

export default AnimalForm;
